import { formatDateToString } from '@/components/TicketsOverviewUI/ticketUiUtils';
import { OrderTicket, TicketHolder } from '@/models/orderTicket';

const OrderTicketCollectionBrand = Symbol('OrderTicketCollectionBrand');

type BaseTicketCollection = {
    // data that all concrete Tickets have in common
    name: OrderTicket['name'];
    duration: OrderTicket['duration'];
    type: OrderTicket['type'];
    startDay: OrderTicket['startDay'];
    validity: OrderTicket['validity'];
    filterTags: OrderTicket['filterTags'];
    infoText: OrderTicket['infoText'];
    photoRequired: OrderTicket['photoRequired'];
    sortKey: OrderTicket['sortKey'];

    numberOfTicketHolders: Readonly<number>;

    // map of productId and OrderTicket
    ticketMap: Record<
        string, // productId
        OrderTicket
    >;

    // key is productId of ticket which is hold by the ticketHolder
    ticketHoldersMap: Record<string, TicketHolder[]>;
};

/**
 * Ticket collection grouped by Ticket name (tickets with the same name but different personTypes are grouped together)
 * It also holds ticketHolders which is a list of customer data with a OrderTicket id reference (of the same collection)
 */
export type OrderTicketCollection = {
    readonly id: string;
    readonly [OrderTicketCollectionBrand]: typeof OrderTicketCollectionBrand;
} & BaseTicketCollection;

export const createOrderTicketCollectionId = (
    name: OrderTicket['name']['de'],
    startDay: OrderTicket['startDay']
) => {
    return `${name}_${formatDateToString(startDay)}`;
};

export const createOrderTicketCollection = (
    createCollection: BaseTicketCollection
) =>
    ({
        ...createCollection,
        id: createOrderTicketCollectionId(
            createCollection.name.de,
            createCollection.startDay
        ),
        [OrderTicketCollectionBrand]: OrderTicketCollectionBrand,
    }) as OrderTicketCollection;

export const getAllOrderTicketsFromCollection = (
    ticketCollection: OrderTicketCollection
): OrderTicket[] => Object.values(ticketCollection.ticketMap);

export const groupOrderTicketsByTicketName = (
    tickets: OrderTicket[]
): OrderTicketCollection[] => {
    const ticketCollectionMap: Record<string, OrderTicketCollection> =
        tickets.reduce<Record<string, OrderTicketCollection>>(
            (collectionContainer, currentTicket) => {
                if (collectionContainer[currentTicket.name.de]) {
                    const ticketCollection =
                        collectionContainer[currentTicket.name.de];
                    collectionContainer[currentTicket.name.de] = {
                        ...ticketCollection,
                        ticketMap: {
                            ...ticketCollection.ticketMap,
                            [currentTicket.productId]: currentTicket,
                        },

                        ticketHoldersMap: {
                            ...ticketCollection.ticketHoldersMap,
                            [currentTicket.productId]: [],
                        },
                    };
                } else {
                    collectionContainer[currentTicket.name.de] =
                        createOrderTicketCollection({
                            name: currentTicket.name,
                            duration: currentTicket.duration,
                            type: currentTicket.type,
                            startDay: currentTicket.startDay,
                            validity: currentTicket.validity,
                            filterTags: currentTicket.filterTags,
                            infoText: currentTicket.infoText,
                            photoRequired: currentTicket.photoRequired,
                            sortKey: currentTicket.sortKey,

                            numberOfTicketHolders: 0,

                            ticketMap: {
                                [currentTicket.productId]: currentTicket,
                            },

                            ticketHoldersMap: {
                                [currentTicket.productId]: [],
                            },
                        });
                }

                return collectionContainer;
            },
            {}
        );

    return Object.values(ticketCollectionMap);
};
